import { cva, type VariantProps } from "class-variance-authority";
import type { ButtonHTMLAttributes, ReactNode } from "react";

import { cn } from "@/lib/cn";
import { Icon, type IconName } from "@/components/icons/Icon";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-1.5 rounded-md font-medium transition-colors whitespace-nowrap disabled:pointer-events-none disabled:opacity-50 cursor-pointer",
  {
    variants: {
      variant: {
        primary:
          "bg-primary text-primary-foreground shadow-sm hover:bg-blue-700",
        secondary:
          "border border-border bg-white text-foreground shadow-sm hover:bg-gray-50",
        ghost:
          "text-muted-foreground hover:bg-gray-100 hover:text-foreground",
        danger:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-red-600",
        default:
          "border border-border bg-white text-foreground shadow-sm hover:bg-gray-50",
      },
      size: {
        sm: "h-7 px-2.5 text-xs",
        md: "h-9 px-4 text-sm",
        lg: "h-11 px-6 text-base",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
    },
  },
);

interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  icon?: IconName;
  children?: ReactNode;
}

const Button = ({
  variant,
  size,
  icon,
  children,
  className,
  ...rest
}: ButtonProps) => (
  <button
    className={cn(buttonVariants({ variant, size }), className)}
    {...rest}
  >
    {icon && <Icon name={icon} size={14} />}
    {children}
  </button>
);

export { Button, buttonVariants };
export type { ButtonProps };
